import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import {
  Appetite_Type_Enum,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import _ from 'lodash';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LatestRatingsPreview from 'src/components/latest-ratings-preview';
import type { ResultProps } from 'src/components/latest-ratings-preview/LatestRatingsPreview';
import AssessmentResultModal from 'src/pages/assessments/modals/AssessmentResultModal';
import type { AssessmentTypeEnum } from 'src/pages/assessments/types';
import ImpactRatingModal from 'src/pages/impacts/ratings/ImpactRatingModal';
import { getPerformanceRatingFromRatingAndAppetite } from 'src/pages/impacts/ratings/performanceCalculation';
import { notEmpty } from 'src/utilityTypes';

import {
  useGetActiveAppetitesByParentId,
  useGetLatestComplianceMonitoringAssessmentRiskAssessmentResultsByRiskId,
  useGetLatestImpactRatingsForRatedImpactsByRatedItemId,
  useGetLatestInternalAuditReportRiskAssessmentResultsByRiskId,
} from '@/hooks/queries';

type Props = {
  riskId: string;
  canViewCompliance: boolean;
  canViewInternalAudit: boolean;
};

const DetailsAside: FC<Props> = ({
  riskId,
  canViewCompliance,
  canViewInternalAudit,
}) => {
  const { t } = useTranslation('common');

  const [selectedAssessmentMode, setSelectedAssessmentMode] =
    useState<AssessmentTypeEnum>('rating');
  const [selectedAssessmentResultId, setSelectedAssessmentResultId] = useState<
    string | undefined
  >();
  const [selectedImpactRatingId, setSelectedImpactRatingId] = useState<
    string | undefined
  >();
  const [showAssessmentResultModal, setShowAssessmentResultModal] =
    useState<boolean>(false);
  const [showImpactRatingModal, setShowImpactRatingModal] =
    useState<boolean>(false);

  const { data: complianceMonitoringResults } =
    useGetLatestComplianceMonitoringAssessmentRiskAssessmentResultsByRiskId({
      queryArgs: { riskId },
      shouldSkip: !canViewCompliance,
    });

  const { data: internalAuditResults } =
    useGetLatestInternalAuditReportRiskAssessmentResultsByRiskId({
      queryArgs: { riskId },
      shouldSkip: !canViewInternalAudit,
    });

  const { data: impactRatingResults } =
    useGetLatestImpactRatingsForRatedImpactsByRatedItemId({
      queryArgs: { ratedItemId: riskId },
    });

  const { data: appetiteData } = useGetActiveAppetitesByParentId({
    queryArgs: { parentId: riskId },
  });

  const activeImpactAppetites = appetiteData?.appetite_parent
    .map((ap) => ap.appetite)
    .filter(notEmpty)
    .filter((a) => a?.AppetiteType === Appetite_Type_Enum.Impact);

  const impactRatingsThatHavePerformance = impactRatingResults?.impact
    .filter((c) => c.ratings.length > 0)
    .map((c) => ({
      ...c,
      performanceRating: getPerformanceRatingFromRatingAndAppetite({
        rating: c.ratings[0].Rating,
        impactAppetite: activeImpactAppetites?.find(
          (a) => a.impact?.Id === c.ratings[0].ImpactId
        )?.ImpactAppetite,
      }),
    }))
    .filter((c) => !_.isNil(c.performanceRating));

  const firstImpactWithRating = impactRatingResults?.impact.find(
    (c) => c.ratings.length > 0
  );

  const showCompliance =
    !!complianceMonitoringResults &&
    (complianceMonitoringResults.controlled.length > 0 ||
      complianceMonitoringResults.uncontrolled.length > 0);
  const showInternalAudit =
    !!internalAuditResults &&
    (internalAuditResults.controlled.length > 0 ||
      internalAuditResults.uncontrolled.length > 0);
  const showLikelihoodPreview = !!firstImpactWithRating;
  const showImpactCategoriesPreview =
    !!impactRatingsThatHavePerformance &&
    impactRatingsThatHavePerformance.length > 0;
  const showImpactRatings =
    showLikelihoodPreview || showImpactCategoriesPreview;

  if (!showCompliance && !showInternalAudit && !showImpactRatings) {
    return null;
  }

  return (
    <>
      <SpaceBetween size={'m'}>
        {showCompliance && (
          <LatestRatingsPreview
            ratingsTitle={t('ratings.complianceRatingSubheading')}
            ratingContext={'oversight'}
            assessmentResults={[
              ...complianceMonitoringResults!.controlled.map<ResultProps>(
                (c) => ({
                  id: c.Id,
                  title: t('assessmentResults.controlTypes.controlled'),
                  rating: c.Rating,
                  ratingType: 'risk_controlled',
                  completionDate: c.TestDate,
                })
              ),
              ...complianceMonitoringResults!.uncontrolled.map<ResultProps>(
                (c) => ({
                  id: c.Id,
                  title: t('assessmentResults.controlTypes.uncontrolled'),
                  rating: c.Rating,
                  ratingType: 'risk_uncontrolled',
                  completionDate: c.TestDate,
                })
              ),
            ]}
            onClick={(id) => {
              setSelectedAssessmentMode('compliance_monitoring_assessment');
              setSelectedAssessmentResultId(id);
              setShowAssessmentResultModal(true);
            }}
          />
        )}
        {showInternalAudit && (
          <LatestRatingsPreview
            ratingsTitle={t('ratings.internalAuditRatingSubheading')}
            ratingContext={'internal_audit'}
            assessmentResults={[
              ...internalAuditResults!.controlled.map<ResultProps>((c) => ({
                id: c.Id,
                title: t('assessmentResults.controlTypes.controlled'),
                rating: c.Rating,
                ratingType: 'risk_controlled',
                completionDate: c.TestDate,
              })),
              ...internalAuditResults!.uncontrolled.map<ResultProps>((c) => ({
                id: c.Id,
                title: t('assessmentResults.controlTypes.uncontrolled'),
                rating: c.Rating,
                ratingType: 'risk_uncontrolled',
                completionDate: c.TestDate,
              })),
            ]}
            onClick={(id) => {
              setSelectedAssessmentMode('internal_audit_report');
              setSelectedAssessmentResultId(id);
              setShowAssessmentResultModal(true);
            }}
          />
        )}
        {showLikelihoodPreview && firstImpactWithRating && (
          <LatestRatingsPreview
            ratingsTitle={''}
            assessmentResults={[
              {
                id: firstImpactWithRating.ratings[0].Id,
                title: t('impactRatings.fields.Likelihood'),
                rating: firstImpactWithRating.ratings[0].Likelihood,
                ratingType: 'likelihood',
                completionDate: firstImpactWithRating.ratings[0].TestDate,
              },
            ]}
            onClick={(id) => {
              setSelectedImpactRatingId(id);
              setShowImpactRatingModal(true);
            }}
          />
        )}
        {showImpactCategoriesPreview && (
          <LatestRatingsPreview
            ratingsTitle={t('impacts.tab_title')}
            assessmentResults={impactRatingsThatHavePerformance!.map<ResultProps>(
              (c) => ({
                id: c.ratings[0].Id,
                title: c.Name,
                rating: c.performanceRating!,
                ratingType: 'impact_performance_rating',
                completionDate: c.ratings[0].TestDate,
              })
            )}
            onClick={(id) => {
              setSelectedImpactRatingId(id);
              setShowImpactRatingModal(true);
            }}
          />
        )}
      </SpaceBetween>
      {showAssessmentResultModal && (
        <AssessmentResultModal
          i18n={t('assessmentResults')}
          id={selectedAssessmentResultId}
          resultType={Parent_Type_Enum.RiskAssessmentResult}
          onDismiss={() => setShowAssessmentResultModal(false)}
          assessmentMode={selectedAssessmentMode}
        />
      )}
      {showImpactRatingModal && (
        <ImpactRatingModal
          impactRatingId={selectedImpactRatingId}
          onDismiss={() => setShowImpactRatingModal(false)}
          onSaving={() => Promise.resolve()}
        />
      )}
    </>
  );
};

export default DetailsAside;
